// Resource types for the litter_page domain.
// Derived from app/views/api/litters/listings/show.json.jbuilder
//
// The litter endpoint renders several of the puppy listing partials verbatim
// (buyer, breeder, owner_stories, photograph, puppy_parent), so those payloads
// are typed once in puppy_page and imported here rather than duplicated. Those
// types move to a shared location with the shared-listing-partials cleanup.
import {
  PuppyAttributeResource,
  PuppyPageBreederResource,
  PuppyPageBuyerResource,
  PuppyPageOwnerStoriesResource,
  PuppyPagePhotographResource,
  PuppyPagePuppyParentResource,
  PuppyPriceBreakdownResource,
  PuppyPageTransportationResource
} from 'dpl/puppy_page/constants/resource_types';

export const LITTER_GALLERY_CATEGORIES = {
  COVER_PHOTO: 'Cover photo',
  PARENTS: 'Parents',
  PAST_LITTER: 'Past litter'
} as const;

export type LitterGalleryCategory =
  (typeof LITTER_GALLERY_CATEGORIES)[keyof typeof LITTER_GALLERY_CATEGORIES];

export interface LitterPageMetaResource {
  type: 'Litter';
  page_title: string;
  page_image: string | null;
  page_description: string;
  page_url: string;
}

/**
 * A litter's gallery is photographs only — an upcoming litter has no videos of
 * its own, and the parents' videos are not part of this grid.
 */
export interface LitterPageGalleryItemResource
  extends PuppyPagePhotographResource {
  category: LitterGalleryCategory;
  /** The parent this photo belongs to; null outside the 'Parents' category. */
  dog_id: number | null;
}

/**
 * The parents' hero photos, mom first then dad. TBD parents are left out
 * entirely and a parent with no profile photo comes through with null urls; the
 * media grid keys its two slots off `role`, so both cases land on the same
 * placeholder without the payload having to distinguish them.
 */
export interface LitterPageHeroPhotoResource {
  dog_id: number;
  name: string;
  role: 'mom' | 'dad';
  /** The gallery entry this photo is, so a slot can open the album on it. */
  photo_id: number | null;
  url: string | null;
  three_two_thumbnail_url: string | null;
}

/**
 * jbuilder emits `{}` rather than `null` for a parent that isn't set, so
 * consumers have to check emptiness rather than nullishness.
 */
export type LitterPageParentResource =
  | PuppyPagePuppyParentResource
  | Record<string, never>;

export interface LitterPageLitterResource {
  id: number;
  status: string;
  breed: string;
  pluralized_breed_name: string;
  breed_id: number;
  /** Month and year only — the page renders this as "Due in {value}". */
  formatted_due_date: string | null;
  description: string | null;
  snoozed: boolean;
  reserved: boolean;
  search_results_url: string;
  hero_photos: LitterPageHeroPhotoResource[];
  gallery: LitterPageGalleryItemResource[];
  price_description: string | null;
  price_breakdown?: PuppyPriceBreakdownResource;
  deposit_description?: string | null;
  deposit_refund_policy?: string | null;
  bnpl_available?: boolean;
  included_extras_paragraphs: string[];
  general_attributes: PuppyAttributeResource[];
  mom: LitterPageParentResource;
  dad: LitterPageParentResource;
}

export interface LitterPageResource {
  meta: LitterPageMetaResource;
  buyer: PuppyPageBuyerResource;
  litter: LitterPageLitterResource;
  breeder: PuppyPageBreederResource;
  transportation: PuppyPageTransportationResource;
  owner_stories: PuppyPageOwnerStoriesResource;
}
